import type { ReviewMode, ReviewModeResponse } from '@annotate/shared';
import type { WidgetConfig } from './config.js';

/**
 * Asking the project whether it is being reviewed.
 *
 * This runs on every page load of every page carrying the tag, including the
 * overwhelming majority where the answer is `off` and nothing happens. Three
 * consequences shape the whole file:
 *
 *  · It fails closed. A timeout, a network error, a 404, a 500 — all of them mean
 *    `off`. A review tool that breaks somebody's site because our container was
 *    restarting is a worse outcome than a review that has to be started twice.
 *
 *  · It fails *quietly*, unless the visitor explicitly asked for review mode. A
 *    console error on every page load of a client's production site is noise they
 *    did not sign up for. Someone who typed `?annotate=1` and got nothing, on the
 *    other hand, is owed an explanation.
 *
 *  · It caches. Without this, a reviewer clicking through twenty pages makes
 *    twenty requests, and so does every real visitor. One per minute per tab is
 *    the trade: ending a review takes up to a minute to reach a page someone is
 *    already sitting on.
 */

const CACHE_KEY = 'annotate.mode-cache';
const TTL_MS = 60_000;
const TIMEOUT_MS = 3_000;

interface Cached {
  /** Cached per project: two projects on one origin must not share an answer. */
  p: string;
  m: ReviewMode;
  t: number;
}

export async function currentMode(config: WidgetConfig): Promise<ReviewMode> {
  const hit = fromCache(config.projectId);
  if (hit) return hit;

  const url = new URL(
    `/api/projects/${encodeURIComponent(config.projectId)}/mode`,
    config.apiBase,
  ).toString();

  try {
    // No headers, deliberately. Adding `Content-Type` would make this a non-simple
    // request and cost a CORS preflight — doubling the round trips on a request
    // that happens on every page load, to send a body that does not exist.
    const res = await fetch(url, { signal: timeout() });
    if (!res.ok) return 'off';

    const body = (await res.json()) as ReviewModeResponse;
    const mode = body.mode === 'link' || body.mode === 'open' ? body.mode : 'off';
    store(config.projectId, mode);
    return mode;
  } catch {
    // Not cached: a failure is a transient state, and caching it would extend a
    // three-second outage into a minute of inertness.
    return 'off';
  }
}

function timeout(): AbortSignal | undefined {
  try {
    return AbortSignal.timeout(TIMEOUT_MS);
  } catch {
    // Older browsers. The fetch simply has no deadline; the page is unaffected
    // either way, since none of this blocks rendering.
    return undefined;
  }
}

function fromCache(projectId: string): ReviewMode | null {
  try {
    const raw = window.sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw) as Cached;
    if (cached.p !== projectId) return null;
    if (Date.now() - cached.t > TTL_MS) return null;
    return cached.m;
  } catch {
    return null;
  }
}

function store(projectId: string, mode: ReviewMode): void {
  try {
    const cached: Cached = { p: projectId, m: mode, t: Date.now() };
    window.sessionStorage.setItem(CACHE_KEY, JSON.stringify(cached));
  } catch {
    /* private browsing — one request per page load instead of one per minute */
  }
}
